import { ActionTree } from "vuex";
import { RootState } from "../../store";
import { PlanTrailState } from "../PlanTrailModule";
import { PlanTrailActionContext } from "./types";
import { CueKind, TurnInstruction } from "../../../../../utils/models/turnInstructions";
import { withCue, withDeletedTurn, withEditedTurn } from "../../../../../utils/models/plannerCues";

interface AddCustomCueData {
  routeId: string | number;
  markerIndex: number;
  text: string;
  sign: number;
  kind: CueKind;
}

/**
 * Which stored entry an edit is aimed at.
 *
 * The displayed list is not the stored list, so a row's position is not an address — see
 * `ManeuverOrigin` in `utils/models/navigation/plannerInstructionParser.ts`.
 */
interface InstructionTarget {
  routeId: string | number;
  segmentIndex: number;
  entryIndex: number;
}

/**
 * Cues the planner added by hand, for the things a router cannot know.
 *
 * Written straight into the segment's stored turns rather than into a list of their own, so
 * they need no new field on the wire and no new code on the reading side: the directions
 * list, the recording banner and the spoken guidance already treat everything in that array
 * as a maneuver. `getPlannedTrailsAttributesForHike` ships it unchanged.
 *
 * Every write here goes through a committed mutation. Nothing needs redrawing — turns are not
 * on the map — but the IndexedDB draft is saved by a subscriber watching mutation *types*, so
 * a direct write to state is invisible to it and the edit does not survive a reload.
 */
export const cueActions: ActionTree<PlanTrailState, RootState> = {
  /**
   * Attach a cue to the control point at `markerIndex`.
   *
   * A marker is the seam between two segments — `lineStrings[i - 1]` ends there and
   * `lineStrings[i]` begins there — so a cue goes at the *start* of the following segment,
   * where the hiker meets it. The last marker has no following segment, so it goes at the
   * end of the one that arrives at it instead.
   */
  addCustomCue({ state, commit }: PlanTrailActionContext, data: AddCustomCueData) {
    const routeKey = Object.keys(state.routes).find(
      (key) => state.routes[key].id === data.routeId
    );
    if (!routeKey) return;

    const route = state.routes[routeKey];
    const lineStrings = route.lineStrings ?? [];
    if (lineStrings.length === 0) return;

    const isLastMarker = data.markerIndex >= lineStrings.length;
    const lineIndex = isLastMarker ? lineStrings.length - 1 : data.markerIndex;
    const lineString = lineStrings[lineIndex];
    if (!lineString) return;

    const coordinates = lineString.lineStringGeometry.coordinates;
    if (!coordinates || coordinates.length < 2) return;

    const distanceAlongMeters = isLastMarker ? Number.MAX_SAFE_INTEGER : 0;

    lineString.lineStringProperties.instructions = withCue(
      lineString.lineStringProperties.instructions ?? [],
      coordinates,
      {
        distanceAlongMeters,
        sign: data.sign,
        text: data.text,
        kind: data.kind
      }
    );

    commitLineStrings(commit, routeKey, lineStrings);
  },

  /**
   * Re-word or re-sign the turn at `target`.
   *
   * An empty `text` clears the override, and GraphHopper's own sentence comes back — the
   * original was never written over.
   */
  editInstruction(
    { state, commit }: PlanTrailActionContext,
    data: InstructionTarget & { text?: string; sign?: number; kind?: CueKind }
  ) {
    rewriteInstructions(state, commit, data, (turns) =>
      withEditedTurn(turns, data.entryIndex, { text: data.text, sign: data.sign, kind: data.kind })
    );
  },

  /**
   * Take the turn at `target` out of the list — removing a cue outright, suppressing a routed
   * turn that a re-snap would otherwise bring straight back.
   */
  deleteInstruction({ state, commit }: PlanTrailActionContext, data: InstructionTarget) {
    rewriteInstructions(state, commit, data, (turns) => withDeletedTurn(turns, data.entryIndex));
  }
};

/**
 * Apply `transform` to one line string's stored turns and commit the result.
 *
 * Committing rather than mutating in place is what makes the change reach the IndexedDB
 * draft: the autosave subscriber in `store/store.ts` watches mutation *types*, so a direct
 * write is invisible to it and the edit is lost on reload.
 */
function rewriteInstructions(
  state: PlanTrailState,
  commit: PlanTrailActionContext['commit'],
  target: InstructionTarget,
  transform: (turns: TurnInstruction[]) => TurnInstruction[]
) {
  const routeKey = Object.keys(state.routes).find(
    (key) => state.routes[key].id === target.routeId
  );
  if (!routeKey) return;

  const lineStrings = state.routes[routeKey].lineStrings ?? [];
  const lineString = lineStrings[target.segmentIndex];
  if (!lineString) return;

  const stored = lineString.lineStringProperties.instructions ?? [];
  const updated = transform(stored);
  if (updated === stored) return;

  lineString.lineStringProperties.instructions = updated;
  commitLineStrings(commit, routeKey, lineStrings);
}

function commitLineStrings(
  commit: PlanTrailActionContext['commit'],
  routeKey: string,
  lineStrings: unknown[]
) {
  // Turns are not drawn on the map, so nothing needs redrawing — but the commit is what the
  // draft autosave listens for.
  commit('setRouteLineStrings', { routeKey, lineStrings });
}
